import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  MouseEvent,
  MouseEventHandler,
  TouchEvent,
  TouchEventHandler,
} from 'react';
import { useTranslation } from 'react-i18next';

import { getPremiumStyled, RETURN_VALUE_ONLY } from 'enrollment/utils';

// Define the props for the Slider component
interface SliderProps {
  id: string;
  min?: number;
  max?: number;
  value?: number;
  extraClass?: string;
  lastNonEoiVolume?: number;
  maxSliderValue?: number;
  increments: {
    increment: string;
    volume: string;
    subscriber_premium: string;
    requires_eoi: boolean;
    allowed_volume: string;
  }[];
  onValueChange: (newVolume: string) => void;
}

// Define the Slider component
export const Slider: React.FC<SliderProps> = ({
  id,
  min = 0,
  max = 100,
  value = 0,
  extraClass = '',
  lastNonEoiVolume = 0,
  maxSliderValue = 100,
  increments,
  onValueChange,
}) => {
  // State to keep track of the slider value
  const [sliderValue, setSliderValue] = useState<number>(value);
  // Reference to the slider container element
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  // Translation hook
  const { t } = useTranslation('pages.volume');

  // Effect to update the value when sliderValue changes
  useEffect(() => {
    if (sliderValue !== value) {
      const nearestIncrement = increments.find(
        (inc) => parseFloat(inc.volume) === sliderValue
      );
      if (nearestIncrement) {
        onValueChange(nearestIncrement.volume);
      }
    }
  }, [sliderValue, increments, onValueChange, value]);

  // Function to update the slider progress
  const updateProgress = useCallback(
    (newLeft: number, rect: DOMRect) => {
      let newValue = (newLeft / rect.width) * (max - min) + min;
      newValue = Math.min(newValue, max);

      const nearestIncrement = increments.reduce((prev, curr) => {
        const currVolume = parseFloat(curr.volume);
        return Math.abs(currVolume - newValue) <
          Math.abs(parseFloat(prev.volume) - newValue)
          ? curr
          : prev;
      });

      setSliderValue(parseFloat(nearestIncrement.volume));

      if (sliderContainerRef.current) {
        const sliderThumb = sliderContainerRef.current.querySelector(
          '.slider-thumb'
        ) as HTMLDivElement;
        const sliderProgress = sliderContainerRef.current.querySelector(
          '.slider-progress'
        ) as HTMLDivElement;
        updateSliderUI(
          parseFloat(nearestIncrement.volume),
          min,
          max,
          sliderThumb,
          sliderProgress
        );
      }
    },
    [min, max, increments, setSliderValue]
  );

  // Handle mouse down events on the slider thumb
  const handleMouseDown: MouseEventHandler<HTMLDivElement> = useCallback(() => {
    const moveListener = (event: MouseEvent) => {
      if (sliderContainerRef.current) {
        const rect = sliderContainerRef.current.getBoundingClientRect();
        const newLeft = event.clientX - rect.left;
        let newValue = (newLeft / rect.width) * (max - min) + min;
        newValue = Math.round(newValue);

        newValue = Math.min(newValue, max);
        newValue = Math.max(newValue, min);

        setSliderValue(newValue);
      }
    };

    const upListener = () => {
      document.removeEventListener('mousemove', moveListener as any);
      document.removeEventListener('mouseup', upListener);
    };

    document.addEventListener('mousemove', moveListener as any);
    document.addEventListener('mouseup', upListener);
  }, [min, max, setSliderValue]);

  // Handle touch start events on the slider thumb
  const handleTouchStart: TouchEventHandler<HTMLDivElement> =
    useCallback(() => {
      const moveListener = (event: TouchEvent) => {
        if (sliderContainerRef.current) {
          const rect = sliderContainerRef.current.getBoundingClientRect();
          const newLeft = event.touches[0].clientX - rect.left;
          let newValue = (newLeft / rect.width) * (max - min) + min;
          newValue = Math.round(newValue);

          newValue = Math.min(newValue, max);
          newValue = Math.max(newValue, min);

          setSliderValue(newValue);
        }
      };

      const upListener = () => {
        document.removeEventListener('touchmove', moveListener as any);
        document.removeEventListener('touchend', upListener);
      };

      document.addEventListener('touchmove', moveListener as any);
      document.addEventListener('touchend', upListener);
    }, [min, max, setSliderValue]);

  // Function to update the slider UI
  function updateSliderUI(
    value: number,
    min: number,
    max: number,
    sliderThumb: HTMLDivElement,
    sliderProgress: HTMLDivElement
  ): void {
    // Calculate the percentage position of the slider thumb
    const percentage = ((value - min) / (max - min)) * 100;
    // Update the slider thumb position
    sliderThumb.style.left = `calc(${percentage}% - 10px)`;
    // Update the slider progress bar width
    sliderProgress.style.width = `${percentage}%`;
  }

  // Handle click events on the slider container
  const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (sliderContainerRef.current) {
        const rect = sliderContainerRef.current.getBoundingClientRect();
        const newLeft = e.clientX - rect.left;
        // Update the slider progress based on the click position
        updateProgress(newLeft, rect);
      }
    },
    [updateProgress]
  );

  // Handle touch end events on the slider container
  const handleTouchEnd: TouchEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (sliderContainerRef.current) {
        const rect = sliderContainerRef.current.getBoundingClientRect();
        const newLeft = e.changedTouches[0].clientX - rect.left;
        // Update the slider progress based on the touch end position
        updateProgress(newLeft, rect);
      }
    },
    [updateProgress]
  );

  // Calculate the width of the guaranteed amount bar
  const guaranteedAmtNum = (lastNonEoiVolume / max) * 100;
  const guaranteedTextOnRight = guaranteedAmtNum <= 12;

  const guaranteedText = (onRight: boolean) => {
    return (
      <div className='pr-3' style={{ paddingLeft: onRight ? '10px' : '0px'}}>
        <p className="body2 mb-0">{t('guaranteed')}</p>
        <div id="amount">
          {getPremiumStyled({
            premium: String(lastNonEoiVolume),
            valueOnly: RETURN_VALUE_ONLY,
          })}
        </div>
      </div>
    );
  };

  // If the guaranteed amount is too close to the max, we want to display the max
  // above the slider to avoid overlap and to correctly display the guaranteed amount
  const guaranteedAmountWidth = (lastNonEoiVolume / max) * 100;
  const displayMaxBelow = guaranteedAmountWidth <= 70;

  return (
    <div>
      {!displayMaxBelow &&
        <div className="d-flex justify-content-end">
          <div className="text-right">
            <p className="body2 mb-0">{t('maximum')}</p>
            <div id="max-amount">
              {' '}
              {getPremiumStyled({
                premium: String(maxSliderValue),
                valueOnly: RETURN_VALUE_ONLY,
              })}
            </div>
          </div>
        </div>
      }
      <div
        className={`slider-container ${extraClass}`}
        ref={sliderContainerRef}
        id={id}
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="slider-progress"
          style={{ width: `${((sliderValue - min) / (max - min)) * 100}%` }}
        ></div>
        <div
          className="slider-thumb"
          style={{
            left: `calc(${((sliderValue - min) / (max - min)) * 100}% - 10px)`,
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <span>
            {getPremiumStyled({
              premium: String(sliderValue),
              valueOnly: RETURN_VALUE_ONLY,
            })}
          </span>
        </div>
      </div>
      <div className="d-flex justify-content-between position-relative">
        {lastNonEoiVolume > 0 && lastNonEoiVolume !== maxSliderValue && (
          <div
            id="guaranteed-amount"
            style={{ width: `${guaranteedAmountWidth}%` }}
          >
            <div className={guaranteedTextOnRight ? 'invisible' : ''}>
              {guaranteedText(guaranteedTextOnRight)}
            </div>
          </div>
        )}
        <div
          id="maximum"
          style={{
            justifyContent: guaranteedTextOnRight
              ? 'space-between'
              : 'flex-end',
          }}
        >
          {lastNonEoiVolume > 0 && guaranteedTextOnRight && guaranteedText(guaranteedTextOnRight)}
          {displayMaxBelow &&
            <div className='text-right'>
              <p className="body2 mb-0">{t('maximum')}</p>
              <div id="max-amount">
                {' '}
                {getPremiumStyled({
                  premium: String(maxSliderValue),
                  valueOnly: RETURN_VALUE_ONLY,
                })}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};
